import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import store from "@src/reducers/store";
import {
  resetStreamingAiResponse,
  updateIsStreamingAiResponse,
  updateModalsCloseDrawerModal,
  updateStreamingAiResponseText,
  updateStreamingAiResponseTools,
  updateStreamingTicketAttachments,
  updateStreamingTicketId,
  updateSupportTicketInformation,
} from "@src/reducers/your-reducer";
import { CustomerTicket } from "@src/types/support";
import { postSupportTicketWithAIStreamUseCase } from "@src/cloud-connect/support/use-cases/post-support-ticket-with-ai-stream-use-case";
import {
  completeRunningTools,
  mergeToolCall,
  normalizeToolCall,
} from "@src/components/molecules/tool-activity-section/stream-tools";
import { useI18N } from "@src/language/language-context";
import {
  clearStreamingTicketLocalFiles,
  setStreamingTicketLocalFiles,
} from "@src/cloud-connect/support/utils/streaming-ticket-local-files";

export interface SubmitSupportTicketParams {
  payload: CustomerTicket;
  contextQueryDatasource?: string | null;
  contextQueryEvents?: string[] | null;
  /** Images attached to the ticket's initial comment (uses the multipart endpoint). */
  files?: File[];
  /** Drawer modal id to close when the ticket arrives */
  closeDrawerModalId?: string;
  /** Opens support chat drawer on ticket creation (default: true) */
  openSupportChatModal?: boolean;
  /** Side effects after ticket is created — e.g. clear form fields */
  onTicketCreated?: () => void;
  genericErrorMessage?: string;
}

export const useCreateSupportTicketStream = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const texts = {
    errorStreamAnswer: i18n("modules.supportChatModal.errorStreamAnswer"),
  };
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  /**
   * True between `ticket` and `done`/`error`. The form drawer closes right after ticket
   * creation, so unmount must not abort while this is true or it'd kill the AI stream.
   */
  const streamLiveRef = useRef(false);
  /** True once the `ticket` event arrived — chat is open; errors go inline like AI RAG. */
  const ticketCreatedRef = useRef(false);

  const cancelStream = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const finishStreamWithInlineError = useCallback(() => {
    const tools = store.getState().yourSlice.streamingAiResponseTools;
    if (tools.length) {
      dispatch(updateStreamingAiResponseTools(completeRunningTools(tools)));
    }
    dispatch(updateStreamingAiResponseText(texts.errorStreamAnswer));
    streamLiveRef.current = false;
    dispatch(updateIsStreamingAiResponse(false));
    setIsSubmitLoading(false);
  }, [dispatch, texts.errorStreamAnswer]);

  const submitSupportTicket = async (params: SubmitSupportTicketParams) => {
    cancelStream();
    streamLiveRef.current = false;
    ticketCreatedRef.current = false;

    const previousTicketId = store.getState().yourSlice.streamingTicketId;
    if (previousTicketId !== null) {
      clearStreamingTicketLocalFiles(previousTicketId);
    }

    const abortController = new AbortController();
    abortRef.current = abortController;

    setIsSubmitLoading(true);
    dispatch(resetStreamingAiResponse());

    const {
      payload,
      contextQueryDatasource,
      contextQueryEvents,
      files,
      closeDrawerModalId,
      openSupportChatModal = true,
      onTicketCreated,
      genericErrorMessage = "Failed to submit technical support request.",
    } = params;

    try {
      for await (const event of postSupportTicketWithAIStreamUseCase(
        payload,
        contextQueryDatasource,
        contextQueryEvents,
        { signal: abortController.signal },
        files,
      )) {
        switch (event.type) {
          case "ticket":
            setStreamingTicketLocalFiles(event.ticket.id, files ?? []);
            dispatch(updateSupportTicketInformation(event.ticket));
            dispatch(updateStreamingTicketId(event.ticket.id));
            dispatch(updateIsStreamingAiResponse(true));
            // Set before closing the drawer below, so its unmount cleanup skips the abort.
            streamLiveRef.current = true;
            ticketCreatedRef.current = true;

            // Close the drawer modal if specified, and open the support chat modal if specified.
            if (closeDrawerModalId) {
              dispatch(
                updateModalsCloseDrawerModal({
                  showModal: false,
                  id: closeDrawerModalId,
                }),
              );
            }

            if (openSupportChatModal) {
              dispatch(
                updateModalsCloseDrawerModal({
                  showModal: true,
                  id: "supportChatDrawerModal",
                }),
              );
            } else {
              clearStreamingTicketLocalFiles(event.ticket.id);
            }

            onTicketCreated?.();
            setIsSubmitLoading(false);
            break;

          case "files":
            // Persisted attachments (with backend thumbnails) of the ticket's
            // initial comment — handed to the support chat via Redux so they
            // render on the first local message.
            dispatch(updateStreamingTicketAttachments(event.attachments));
            break;

          case "token": {
            // A token while a tool is still running means that tool just finished.
            const tools = store.getState().yourSlice.streamingAiResponseTools;
            if (tools.length) {
              dispatch(
                updateStreamingAiResponseTools(completeRunningTools(tools)),
              );
            }
            dispatch(
              updateStreamingAiResponseText(
                store.getState().yourSlice.streamingAiResponseText + event.text,
              ),
            );
            break;
          }

          case "tool-call": {
            const tools = store.getState().yourSlice.streamingAiResponseTools;
            dispatch(
              updateStreamingAiResponseTools(
                normalizeToolCall(event.data).reduce(mergeToolCall, tools),
              ),
            );
            break;
          }

          case "done": {
            const tools = store.getState().yourSlice.streamingAiResponseTools;
            if (tools.length) {
              dispatch(
                updateStreamingAiResponseTools(completeRunningTools(tools)),
              );
            }
            streamLiveRef.current = false;
            dispatch(updateIsStreamingAiResponse(false));
            setIsSubmitLoading(false);
            break;
          }

          case "error":
            if (ticketCreatedRef.current) {
              // Chat already open — same as AI RAG: show error in the bubble, keep ticket id.
              finishStreamWithInlineError();
            } else {
              streamLiveRef.current = false;
              errorSnackbar(event.message);
              dispatch(resetStreamingAiResponse());
              setIsSubmitLoading(false);
            }
            return;
        }
      }

      if (!abortController.signal.aborted) {
        dispatch(updateIsStreamingAiResponse(false));
        setIsSubmitLoading(false);
      }
    } catch (err) {
      streamLiveRef.current = false;
      if (!abortController.signal.aborted) {
        console.error("Stream error:", err);
        if (ticketCreatedRef.current) {
          finishStreamWithInlineError();
        } else {
          errorSnackbar(genericErrorMessage);
          dispatch(resetStreamingAiResponse());
          setIsSubmitLoading(false);
        }
      }
    } finally {
      if (abortRef.current === abortController) {
        abortRef.current = null;
      }
    }
  };

  // Cancel on unmount unless a ticket-creation stream is actively flowing (see streamLiveRef).
  useEffect(
    () => () => {
      if (!streamLiveRef.current) {
        cancelStream();
      }
    },
    [cancelStream],
  );

  return { submitSupportTicket, cancelStream, isSubmitLoading };
};
